import { useState, useEffect, useCallback } from "react";
import { CheckCircle, Circle, Users, RefreshCw, Undo2, ShieldCheck, Search, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FoodPaymentsPage = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tuesdayInfo, setTuesdayInfo] = useState(null);
  const [search, setSearch] = useState("");

  const getAuthHeaders = useCallback(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  const fetchOrders = useCallback(async () => {
    try {
      const [ordersRes, infoRes] = await Promise.all([
        axios.get(`${API}/food-orders/list`, { headers: getAuthHeaders() }),
        axios.get(`${API}/food-orders/next-tuesday`),
      ]);
      setOrders(ordersRes.data);
      setTuesdayInfo(infoRes.data);
    } catch {} finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleCheckin = async (orderId) => {
    try {
      await axios.put(`${API}/food-orders/${orderId}/checkin`,
        { checked_in: true },
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
      setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, checked_in: true } : o));
      toast.success("Tjekket ind!");
    } catch (err) {
      const msg = err.response?.data?.detail || "Kunne ikke opdatere";
      toast.error(msg);
    }
  };

  const handleUndo = async (orderId) => {
    try {
      await axios.put(`${API}/food-orders/${orderId}/checkin`,
        { checked_in: false },
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
      setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, checked_in: false } : o));
      toast.success("Check-in fortrudt");
    } catch (err) {
      const msg = err.response?.data?.detail || "Kunne ikke fortryde";
      toast.error(msg);
    }
  };

  const filtered = search.trim()
    ? orders.filter(o => o.name.toLowerCase().includes(search.toLowerCase()) || (o.payment_code || '').toLowerCase().includes(search.toLowerCase()))
    : orders;
  const notCheckedIn = filtered.filter(o => !o.checked_in);
  const checkedIn = filtered.filter(o => o.checked_in);
  const total = orders.length;

  return (
    <div className="min-h-screen pt-24 pb-16 px-4"
      style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(30,30,30,1) 0%, rgba(10,10,10,1) 60%)" }}>
      <div className="max-w-2xl mx-auto">

        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-white transition-all mb-2" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" /> Tilbage
            </button>
            <h1 className="text-3xl sm:text-4xl font-['Oswald'] font-black text-white tracking-wider" data-testid="food-payments-title">
              Mad betalinger
            </h1>
            {tuesdayInfo && (
              <p className="text-gray-400 text-sm mt-1">Tirsdag {tuesdayInfo.event_date_formatted}</p>
            )}
          </div>
          <button onClick={() => { setLoading(true); fetchOrders(); }} className="p-2 rounded-lg text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 transition-all" data-testid="refresh-orders-btn">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-black/30 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Users className="w-5 h-5 text-amber-400" />
              <span className="text-2xl font-['Oswald'] font-black text-white" data-testid="stat-total">{total}</span>
            </div>
            <p className="text-gray-500 text-xs uppercase tracking-wider">Bestilt</p>
          </div>
          <div className="bg-black/30 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Circle className="w-5 h-5 text-orange-400" />
              <span className="text-2xl font-['Oswald'] font-black text-white" data-testid="stat-waiting">{notCheckedIn.length}</span>
            </div>
            <p className="text-gray-500 text-xs uppercase tracking-wider">Venter</p>
          </div>
          <div className="bg-black/30 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <span className="text-2xl font-['Oswald'] font-black text-white" data-testid="stat-checked-in">{checkedIn.length}</span>
            </div>
            <p className="text-gray-500 text-xs uppercase tracking-wider">Tjekket ind</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Søg efter navn eller kode..."
            className="w-full bg-black/30 border border-white/10 text-white text-sm rounded-xl pl-10 pr-4 py-3 focus:border-amber-500/50 focus:outline-none placeholder-gray-600"
            data-testid="search-orders"
          />
        </div>

        {loading ? (
          <p className="text-gray-500 text-sm text-center py-8">Henter bestillinger...</p>
        ) : orders.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-8">Ingen bestillinger endnu</p>
        ) : (
          <>
            {/* NOT CHECKED IN section */}
            <div className="mb-6" data-testid="not-checked-in-section">
              <div className="flex items-center gap-2 mb-3 px-1">
                <Circle className="w-4 h-4 text-orange-400" />
                <h2 className="text-base font-['Oswald'] font-bold text-white uppercase tracking-wider">
                  Ikke tjekket ind ({notCheckedIn.length})
                </h2>
              </div>
              {notCheckedIn.length === 0 ? (
                <div className="bg-black/30 border border-white/10 rounded-xl p-6 text-center">
                  <ShieldCheck className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-emerald-400 text-sm">Alle er tjekket ind!</p>
                </div>
              ) : (
                <div className="bg-black/30 backdrop-blur-sm border border-orange-500/20 rounded-xl overflow-hidden" data-testid="not-checked-in-list">
                  <div className="divide-y divide-white/5">
                    {notCheckedIn.map(order => (
                      <div key={order.order_id} className="flex items-center gap-3 p-4" data-testid={`order-row-${order.order_id}`}>
                        <button
                          onClick={() => handleCheckin(order.order_id)}
                          className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-black/40 border-2 border-orange-500/50 text-orange-400 hover:border-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all"
                          data-testid={`checkin-btn-${order.order_id}`}
                        >
                          <Circle className="w-5 h-5" />
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium text-sm">
                            {order.name}
                            {order.quantity > 0 ? ` (${order.quantity} voksne` : ''}
                            {order.child_quantity > 0 ? `${order.quantity > 0 ? ' + ' : '('}${order.child_quantity} børn` : ''}
                            {(order.quantity > 0 || order.child_quantity > 0) ? ')' : ''}
                          </p>
                          <p className="text-gray-500 text-xs font-mono">Kode: {order.payment_code}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-amber-400 font-bold text-sm">{order.amount} kr</span>
                          <p className="text-orange-400 text-xs">Betalt</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* CHECKED IN section */}
            {checkedIn.length > 0 && (
              <div data-testid="checked-in-section">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <h2 className="text-base font-['Oswald'] font-bold text-white uppercase tracking-wider">
                    Tjekket ind ({checkedIn.length})
                  </h2>
                </div>
                <div className="bg-black/30 backdrop-blur-sm border border-emerald-500/20 rounded-xl overflow-hidden" data-testid="checked-in-list">
                  <div className="divide-y divide-white/5">
                    {checkedIn.map(order => (
                      <div key={order.order_id} className="flex items-center gap-3 p-4 bg-emerald-500/5" data-testid={`order-row-${order.order_id}`}>
                        <div className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-emerald-500 text-white">
                          <CheckCircle className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-emerald-400 font-medium text-sm">
                            {order.name}
                            {order.quantity > 0 ? ` (${order.quantity} voksne` : ''}
                            {order.child_quantity > 0 ? `${order.quantity > 0 ? ' + ' : '('}${order.child_quantity} børn` : ''}
                            {(order.quantity > 0 || order.child_quantity > 0) ? ')' : ''}
                          </p>
                          <p className="text-gray-500 text-xs font-mono">Kode: {order.payment_code}</p>
                        </div>
                        <button
                          onClick={() => handleUndo(order.order_id)}
                          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-orange-400 bg-orange-500/10 border border-orange-500/20 hover:bg-orange-500/20 transition-all"
                          data-testid={`undo-btn-${order.order_id}`}
                        >
                          <Undo2 className="w-3.5 h-3.5" /> Retur
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FoodPaymentsPage;
